import Cookies from 'js-cookie'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import LogoIcon from '@/assets/images/logo-icon.svg?react'
import { message } from '@/components'
import { Button, Input, Trans } from '@/components'
import { MfaChallengeModal } from '@/components/mfa/MfaChallengeModal'
import { PLATFORM_HOME_PATH } from '@/consts/routes'
import useOauth, { OauthType } from '@/hooks/use-oauth'
import { useAuthStore } from '@/stores/auth'
import { persistAdAttribution, persistAdAttributionFromCurrentUrl } from '@/utils/adAttribution'
import { getErrorCodeMessage } from '@/utils/error'
import { cn } from '@/utils/style'

export function LoginPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const authStore = useAuthStore()
  const [loading, setLoading] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  const { oauthCode, oauthType, GoogleLoginButton, GithubLoginButton } = useOauth()
  const [inviterCode, setInviterCode] = useState('')
  const [gclid, setGclid] = useState('')
  const [showMfaModal, setShowMfaModal] = useState(false)
  const [mfaChallenge, setMfaChallenge] = useState('')
  const urlSearchParams = new URLSearchParams(location.search)
  // Check both URL parameter and sessionStorage for multi-account mode
  const urlHasMultiAccount = urlSearchParams.has('multi-account')
  const sessionHasMultiAccount = sessionStorage.getItem('isMultiAccountMode') === '1'
  const isOauthCallback = urlSearchParams.has('code')
  const isMultiAccountMode = urlHasMultiAccount || (isOauthCallback && sessionHasMultiAccount)

  const handleLoginSuccess = useCallback(() => {
    // Clean up multi-account mode flag from sessionStorage
    sessionStorage.removeItem('isMultiAccountMode')

    if (isMultiAccountMode) {
      // In multi-account mode, redirect back to main app after successful login
      navigate(PLATFORM_HOME_PATH)
    }
    // For normal login mode, AuthLayout will handle the redirect automatically
  }, [isMultiAccountMode, navigate])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    persistAdAttribution(searchParams)
    if (searchParams.has('inviter_code')) {
      setInviterCode(searchParams.get('inviter_code') || '')
    }
    if (searchParams.has('gclid')) {
      setGclid(searchParams.get('gclid') || '')
    }
  }, [location.search])

  useEffect(() => {
    const inviterCodeCookie = Cookies.get('inviter_code')
    if (!oauthCode) {
      return
    }
    const provider =
      oauthType === OauthType.Google
        ? 'google'
        : oauthType === OauthType.Github
          ? 'github'
          : undefined
    if (!provider) {
      return
    }
    setLoading(true)
    // In multi-account mode, always save the account
    const saveAccount = isMultiAccountMode || true
    authStore
      .loginByOauth(oauthCode, provider, inviterCodeCookie, saveAccount, {
        shouldRedirectNewUserToWelcome: !isMultiAccountMode,
      })
      .then(() => {
        handleLoginSuccess()
      })
      .catch((error) => {
        console.error('OAuth login failed:', error)
        message.error('OAuth login failed. Please try again.')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [oauthCode, oauthType, isMultiAccountMode, handleLoginSuccess])

  const onLogin = useCallback(() => {
    if (loading) {
      return
    }

    // Validation
    if (!email.trim()) {
      message.error('Please enter your email address')
      return
    }
    if (!password.trim()) {
      message.error('Please enter your password')
      return
    }

    setLoading(true)
    const saveAccount = isMultiAccountMode || true
    authStore
      .loginStep1(email, password)
      .then((result) => {
        if (result.mfaRequired) {
          setMfaChallenge(result.mfaChallenge)
          setShowMfaModal(true)
          return
        }
        return authStore
          ._finalizeLogin(result.token, email, undefined, saveAccount, {
            shouldRedirectNewUserToWelcome: !isMultiAccountMode,
          })
          .then(() => handleLoginSuccess())
      })
      .catch((error) => {
        console.error('Login failed:', error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [authStore, email, password, isMultiAccountMode, handleLoginSuccess])

  const handleMfaExpired = () => {
    setShowMfaModal(false)
    setMfaChallenge('')
    message.error(getErrorCodeMessage(240, t))
  }

  return (
    <div className="flex flex-col items-center gap-y-8 md:px-6">
      <div className="card bg-panel-white w-[480px] md:w-full flex flex-col items-center gap-4">
        <div className="w-full py-2 flex flex-col items-center gap-4">
          <LogoIcon className="auth-logo-icon w-16 h-16" />
          <h2 className="text-primary h5 text-center">
            <Trans i18nKey="login.title">Welcome To Glows.ai</Trans>
          </h2>

          <GoogleLoginButton
            loading={loading}
            disabled={import.meta.env.VITE_ENV !== 'production'}
            inviterCode={inviterCode}
            gclid={gclid}
          />
          <GithubLoginButton loading={loading} inviterCode={inviterCode} gclid={gclid} />

          <div className="flex w-full h-9 items-center">
            <div className={cn('flex-grow h-0 border-b border-gray100')}></div>
            <div className="w-[160px] text-tertiary text-center t-sm">Or sign in with Email</div>
            <div className={cn('flex-grow h-0 border-b border-gray100')}></div>
          </div>

          <div className="w-full t-lg text-primary">
            <div className="mb-2">
              <Trans i18nKey="login.labelEmail">Email</Trans>
            </div>
            <Input
              placeholder="Email address"
              value={email}
              disabled={loading}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="w-full t-lg text-primary">
            <div className="mb-2">
              <Trans i18nKey="login.labelPassword">Password</Trans>
            </div>
            <Input
              placeholder="Password"
              type="password"
              value={password}
              disabled={loading}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onLogin()
                }
              }}
            />
          </div>

          <Button type="secondary" block className="mt-2" onClick={onLogin} disabled={loading}>
            <Trans i18nKey="login.loginBtn">Login</Trans>
          </Button>

          <div className="flex mt-4 t-lg md:t-sm gap-2">
            <Trans i18nKey="register.toRegister">
              Don't have an account yet?
              <Link
                to={inviterCode === '' ? '/register' : `/register?inviter_code=${inviterCode}`}
                className="text-btn-secondary font-bold"
                onClick={() => {
                  Cookies.set('inviter_code', inviterCode)
                  persistAdAttributionFromCurrentUrl()
                }}
              >
                Sign up
              </Link>
            </Trans>
          </div>
        </div>
      </div>
      <div className="max-w-[480px] text-center t-sm text-secondary">
        <Trans i18nKey="login.agreement">
          You acknowledge that you read, and agree to our
          <a className="text-primary" href="https://glows.ai/eula">
            EULA
          </a>
          ,
          <a className="text-primary" href="https://glows.ai/tos">
            Terms of Service
          </a>
          and
          <a className="text-primary" href="https://glows.ai/privacy">
            Privacy Policy
          </a>
        </Trans>
      </div>

      <MfaChallengeModal
        open={showMfaModal}
        mfaChallenge={mfaChallenge}
        email={email}
        saveAccount={isMultiAccountMode || true}
        shouldRedirectNewUserToWelcome={!isMultiAccountMode}
        onSuccess={() => {
          setShowMfaModal(false)
          setMfaChallenge('')
          handleLoginSuccess()
        }}
        onBack={() => {
          setShowMfaModal(false)
          setMfaChallenge('')
        }}
        onExpired={handleMfaExpired}
      />
    </div>
  )
}
